// TODO: Enable eslint and add unit tests to cover uncovered condition
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { DndContext, DragOverlay } from '@dnd-kit/core';
import frLocale from '@fullcalendar/core/locales/fr';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { ThirdPartyDraggable } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash/debounce';

import PropTypes from 'prop-types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

// Hooks
import useSubmitPost from '@/hooks/scheduler/useSubmitPost';
import {
    UPDATE_CALENDAR_MEDIA_LIBRARY,
    UPDATE_CALENDAR_TITLE_DATE,
    UPDATE_CALENDAR_VIEW,
    useCalendar
} from '@/utils/hooks/scheduler/useCalendar';
import useEventDrop from '@/utils/hooks/scheduler/useEventDrop';
import useFoBttpEvents from '@/utils/hooks/scheduler/useFoBttpEvents';
import useSchedulerPosts from '@/utils/hooks/scheduler/useSchedulerPosts';
import useSocialMediaEvents from '@/utils/hooks/scheduler/useSocialMediaEvents';
import useTimeSlots from '@/utils/hooks/scheduler/useTimeSlots';
import useUser from '@/utils/hooks/swr/useUser';
import { useGetFunnelCreationStatus } from '@/utils/scheduler/funnel/hooks/useGetFunnelCreationStatus';

import { classNames } from '@/utils';
//utils
import { getCalendarViewLocalStorage } from '@/utils/localStorage/scheduler/calendarViewLocalStorage';
import { mixPanelSchedulerNewPost } from '@/utils/mixpanel/schedulerEvents';
import { formatLibraryMediaForPost } from '@/utils/scheduler/funnel/mediaLibrary/formatLibraryMediaForPost';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import useToggle from '@/utils/hooks/utils/useToggle';

// Lib
import { calendarViews, dayOfWeek } from '@/lib/scheduler/calendar/calendar';
import { DRAFT } from '@/lib/scheduler/postStatus';
import { SCHEDULER_QUERY_ACTION_PARAM } from '@/lib/scheduler/queryParams';
import { INSTAGRAM, TIKTOK_BUSINESS } from '@/lib/socialProfile';

// Components
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import CalendarCellMonth from '@/components/composite/Scheduler/Calendar/CalendarCellMonth';
import DayHeader from '@/components/composite/Scheduler/Calendar/DayHeader';
import PostEvent from '@/components/composite/Scheduler/Calendar/PostEvent';
import PostEventStacked from '@/components/composite/Scheduler/Calendar/PostEventStacked';
import SocialMediaEvent from '@/components/composite/Scheduler/Calendar/SocialMediaEvent';
import TimeLabel from '@/components/composite/Scheduler/Calendar/TimeLabel';
import UtcDisplay from '@/components/composite/Scheduler/Calendar/UtcDisplay';
import CalendarHeader from '@/components/pages/scheduler/calendar/CalendarHeader';
import PostEventSettingsPopover from '@/components/pages/scheduler/calendar/PostEventSettingsPopover';
import WeekDayCells from '@/components/pages/scheduler/calendar/WeekDayCells';
import { ImageMedia } from '@/components/pages/scheduler/medialibrary/MediaLibraryScheduler';
import { MediaLibraryWrapper } from '@/components/pages/scheduler/medialibrary/MediaLibraryWrapper';

const SchedulerFunnel = dynamic(() =>
    import('@/components/composite/Scheduler/Funnel').then((mod) => mod.SchedulerFunnel)
);
const ModalPostPreview = dynamic(
    () => import('@/components/pages/scheduler/calendar/preview/ModalPostPreview')
);
const ModalDragAndDropPostCrosspost = dynamic(
    () => import('@/components/pages/scheduler/calendar/ModalDragAndDropPostCrosspost')
);

function scrollToRef(el) {
    setTimeout(
        el?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        }),
        600
    );
}

const CalendarView = ({
    isGroup,
    activeGroup,
    activeSocialProfile,
    activatedSocialProfiles,
    spGroups,
    needReloadScheduledPosts = false,
    setNeedReloadScheduledPosts = () => {},
    blockView = false,
    hasSchedulerPermission
}) => {
    const [calendarDate, setCalendarDate] = useState(null);
    const [showMoreEvent, setShowMoreEvent] = useState({
        state: false,
        shouldShowMore: false,
        isShowingMore: false
    });
    const [hasBeenMount, setHasBeenMount] = useState(false);
    const [nowMounted, setNowMounted] = useState(false);
    const [mediaLibraryState, setMediaLibraryState] = useState({
        hasBeenInitialised: false,
        activeMedia: null,
        isActive: false,
        dateStr: '',
        isMediaLibraryPostingLoading: false,
        transitionInProgress: false
    });
    const {
        isOpen: isOpenMediaLibraryError,
        toggle: toggleMediaLibraryError,
        params: paramsMediaLibraryError,
        setParams: setParamsMediaLibraryError
    } = useToggle({
        initial: false
    });
    const calendarRef = useRef();
    const nowIndicatorRef = useRef();
    const { submitSinglePost } = useSubmitPost({ forcedSocialProfile: activeSocialProfile });
    const {
        isOpen: isOpenPostFunnel,
        toggle: togglePostFunnel,
        params: paramsPostFunnel,
        setParams: setParamsPostFunnel
    } = useToggle();
    const {
        isOpen: isOpenPostPreview,
        toggle: togglePostPreview,
        params: paramsPostPreview,
        setParams: setParamsPostPreview
    } = useToggle();
    const { calendarData, dispatchCalendar } = useCalendar();
    const { eventsHandling } = useEventDrop();
    const { user } = useUser();
    const userTimezone = user?.settings?.timezone ?? 'Europe/Paris';
    /**
     * profiles for funnel and creation status either create or create_reminder
     */
    const { profiles, creation_type } = useGetFunnelCreationStatus({
        activeSocialProfile,
        activeGroup,
        isGroup
    });

    // CrossPost drag and drop validate modal data
    const { isOpen: isOpenCrosspostDragAndDropModal, toggle: toggleCrosspostDragAndDropModal } =
        useToggle();
    const [crossPostDragAndDropModalData, setCrossPostDragAndDropModalData] = useState(null);

    // Current time rounded
    const currentTime = dayjs().utc().tz(userTimezone).minute(0).second(0).millisecond(0);

    // Fetch calendar posts
    const { schedulerPosts, schedulerStackedPosts, isLoadingSchedulerPosts, mutateSchedulerPosts } =
        useSchedulerPosts({
            [isGroup ? 'activeGroup' : 'activeSPID']: isGroup
                ? activeGroup
                : activeSocialProfile?.id,
            params: calendarDate,
            filter_post_status: calendarData.filter_post_status
        });

    // Fetch FO & BTTP
    const { followersOnlineData, bestTimeToPostData } = useFoBttpEvents(
        isGroup ? { activeGroupID: activeGroup?.id } : { activeSPID: activeSocialProfile?.id }
    );

    // Fetch social media events
    const { socialMediaEvents, eventsPerDay } = useSocialMediaEvents();

    // Fetch time slots
    const { timeSlotsFormatted } = useTimeSlots({
        isGroup,
        activeGroup,
        activeSocialProfile
    });

    // Query params handlers
    const router = useRouter();
    const { preview_post_id } = router.query;
    const queryAction = router.query[SCHEDULER_QUERY_ACTION_PARAM.NAME];
    const { deleteQuery } = useRouterQuery();

    // Innit external dragging event
    const fullCalendarDraggableInit = () => {
        let mediaLibraryContainer = document.querySelector('.scheduler-media-library');

        new ThirdPartyDraggable(mediaLibraryContainer, {
            itemSelector: '.draggable-media',
            mirrorSelector: '',
            eventData: () => ({
                create: true // DO NOT FORGET THIS PARAM => https://fullcalendar.io/docs/external-dragging
            })
        });
    };

    // Creates a draft post when dropping media from media library panel
    // This event is debounced because sometimes duplicates are rendered
    const onDropMedia = debounce(async (dropInfo) => {
        // Current drop slot infos
        const time = dayjs(dropInfo?.dateStr ?? mediaLibraryState.activeMedia.dateStr).format(
            'YYYY-MM-DDTHH:mm:ss'
        );
        const currentHour = dayjs().utc().tz(userTimezone);
        const utc = dayjs(dropInfo?.dateStr ?? mediaLibraryState.activeMedia.dateStr)
            .utc()
            .tz(userTimezone)
            .format('Z');
        const dateSlot = dayjs(time + utc).utc();

        // Is monthly view active
        const isMonthlyView = getCalendarViewLocalStorage() === calendarViews.month;

        // Allow dropping the same day / hour
        const isToday =
            dayjs(dropInfo?.dateStr ?? mediaLibraryState.activeMedia.dateStr).format(
                `YYYY-MM-DD${!isMonthlyView ? ' HH' : ''}`
            ) === currentTime.format(`YYYY-MM-DD${!isMonthlyView ? ' HH' : ''}`);
        // Is element dragging from the media library panel
        const isFromMediaLibrary =
            dropInfo?.draggedEl?.classList.contains('draggable-media') ?? dropInfo.isRetry;

        if (isFromMediaLibrary) {
            const isTiktokMedia =
                activeSocialProfile?.social_network_type === TIKTOK_BUSINESS ? true : undefined;
            setMediaLibraryState((prevState) => ({
                ...prevState,
                activeMedia: {
                    ...prevState.activeMedia,
                    dateStr: dropInfo?.dateStr ?? prevState.activeMedia.dateStr ?? ''
                },
                isMediaLibraryPostingLoading: true
            }));

            const dataMedia = dropInfo?.draggedEl
                ? JSON.parse(dropInfo?.draggedEl?.getAttribute('data-media'))
                : dropInfo;

            const formattedDataMedia = formatLibraryMediaForPost({
                id: dataMedia.id,
                medias: [dataMedia.media]
            });

            const caption = dropInfo?.draggedEl?.getAttribute('data-caption');
            const postType = dropInfo?.draggedEl?.getAttribute('data-post-type');

            const scheduled_at = isMonthlyView
                ? dateSlot.add(currentHour.utc().tz(userTimezone).hour() + 2, 'hour').format()
                : isToday
                  ? currentHour.utc().add(5, 'minute').format()
                  : dateSlot.format();

            let postState = DRAFT;

            /**
             * new scheduler
             */
            if (creation_type === 'create_reminder') {
                postState = 'reminder';
            }

            const post = {
                type: postType,
                scheduled_at,
                scheduled_at_option: 'custom',
                autopost: true,
                state: postState,
                data: {
                    enable_comment: isTiktokMedia,
                    enable_duet: isTiktokMedia,
                    enable_stitch: isTiktokMedia,
                    caption: caption ?? undefined,
                    medias: [formattedDataMedia]
                }
            };

            await submitSinglePost(
                post,
                activeSocialProfile.id,
                () => {
                    if (isOpenMediaLibraryError) {
                        toggleMediaLibraryError();
                    }

                    setMediaLibraryState((prevState) => ({
                        ...prevState,
                        isMediaLibraryPostingLoading: false
                    }));
                    mutateSchedulerPosts(null);
                },
                (e) => {
                    if (!isOpenMediaLibraryError) {
                        toggleMediaLibraryError();
                        //TODO Remove after prod migration
                        if (e?.error === 'migration-upgrade-area-error') {
                            setParamsMediaLibraryError(
                                'scheduler:scheduler_funnel.submit_errors.migration_upgrade_area_error_banner'
                            );
                        } else {
                            setParamsMediaLibraryError(null);
                        }
                    }

                    setMediaLibraryState((prevState) => ({
                        ...prevState,
                        isMediaLibraryPostingLoading: false
                    }));
                },
                false
            );

            // mixpanel : track new post creation from media drop
            mixPanelSchedulerNewPost('media_drop');
        }
    }, 10);

    useEffect(() => {
        if (preview_post_id && !isOpenPostPreview && activeSocialProfile?.id) {
            setParamsPostPreview({
                postId: preview_post_id
            });
            togglePostPreview();
            router.replace(deleteQuery('preview_post_id'));
        }
    }, [preview_post_id]);

    useEffect(() => {
        if (queryAction === SCHEDULER_QUERY_ACTION_PARAM.VALUES.CREATE_POST) {
            /**
             * initialize funnel params
             */

            setParamsPostFunnel({
                type: creation_type,
                socialProfiles: profiles
            });

            togglePostFunnel();
            const newUrl = deleteQuery(SCHEDULER_QUERY_ACTION_PARAM.NAME);
            router.replace(newUrl);
        }
    }, [queryAction]);

    useEffect(() => {
        if (needReloadScheduledPosts) {
            setNeedReloadScheduledPosts(false);
            mutateSchedulerPosts(null);
        }
    }, [mutateSchedulerPosts, needReloadScheduledPosts, setNeedReloadScheduledPosts]);

    useEffect(() => {
        const calendarApi = calendarRef.current.getApi();
        const eventSourceSmc = calendarApi.getEventSourceById('SMC');

        if (eventSourceSmc) {
            eventSourceSmc.remove();
        }

        // SMC events
        if (calendarData.events.smc.active && socialMediaEvents) {
            const smcEvents = {
                id: 'SMC',
                events: socialMediaEvents
            };
            calendarApi.addEventSource(smcEvents);
        }
    }, [calendarData.events.smc.active, socialMediaEvents]);

    useEffect(() => {
        const calendarApi = calendarRef.current.getApi();
        const eventSourcePosts = calendarApi.getEventSourceById('POST');

        if (eventSourcePosts) {
            eventSourcePosts.remove();
        }

        // SMC events
        if (schedulerPosts && !isLoadingSchedulerPosts && schedulerStackedPosts) {
            const postsEvents = {
                id: 'POST',
                events: schedulerPosts
            };
            calendarApi.addEventSource(postsEvents);
        }
    }, [schedulerPosts, isLoadingSchedulerPosts, schedulerStackedPosts]);

    useEffect(() => {
        const calendarApi = calendarRef.current.getApi();

        if (!calendarData.openMediaLibrary.active) {
            setMediaLibraryState((prevState) => ({
                ...prevState,
                hasBeenInitialised: false
            }));
        }

        if (
            calendarApi &&
            !isLoadingSchedulerPosts &&
            !mediaLibraryState.hasBeenInitialised &&
            calendarData.openMediaLibrary.active
        ) {
            setMediaLibraryState((prevState) => ({
                ...prevState,
                hasBeenInitialised: true
            }));

            setTimeout(() => fullCalendarDraggableInit(), 600);
        }
    }, [isLoadingSchedulerPosts, isLoadingSchedulerPosts, calendarData.openMediaLibrary.active]);

    useEffect(() => {
        const wrapperElement = document?.querySelector('.scheduler-wrapper-element');
        if (blockView) {
            wrapperElement?.classList?.add('overflow-hidden', 'max-h-screen');
        } else if (
            isLoadingSchedulerPosts &&
            wrapperElement &&
            !hasBeenMount &&
            !wrapperElement?.classList?.contains('overflow-hidden')
        ) {
            wrapperElement?.classList?.add('overflow-hidden', 'max-h-screen');
        } else if (
            !isLoadingSchedulerPosts &&
            !hasBeenMount &&
            wrapperElement?.classList?.contains('overflow-hidden') &&
            !blockView
        ) {
            wrapperElement?.classList?.remove('overflow-hidden', 'max-h-screen');
            if (nowMounted) {
                //Scroll to now when scroll is available again
                requestAnimationFrame(function () {
                    //scroll to date only if not initially visible
                    if (dayjs().utc().tz(userTimezone).format('H') >= 6) {
                        scrollToRef(nowIndicatorRef.current);
                    }
                });
            }
            setHasBeenMount(true);
        }
    }, [isLoadingSchedulerPosts, hasBeenMount, userTimezone, nowMounted, blockView]);

    useEffect(() => {
        if (!socialMediaEvents || calendarData?.view === calendarViews?.month) {
            return;
        }

        //get displayed dates that have more events to display
        const hasMoreThanTwoEvents =
            calendarData?.view === calendarViews?.week
                ? Object.entries(eventsPerDay)?.filter(
                      (el) =>
                          (dayjs(el?.[0])?.isBetween(calendarDate?.start, calendarDate?.end) ||
                              dayjs(el?.[0])?.isSame(calendarDate?.start) ||
                              dayjs(el?.[0])?.isSame(calendarDate?.end)) &&
                          el?.[1]?.length > 2
                  )
                : eventsPerDay?.[calendarDate?.start]?.length > 2
                  ? eventsPerDay?.[calendarDate?.start]
                  : [];

        if (!hasMoreThanTwoEvents?.length) {
            // If nothing more to display and options activated deactivate it
            if (
                showMoreEvent?.shouldShowMore ||
                showMoreEvent?.state ||
                showMoreEvent?.isShowingMore
            ) {
                setShowMoreEvent({
                    state: false,
                    shouldShowMore: false,
                    isShowingMore: false
                });
            }
        } else if (!showMoreEvent?.shouldShowMore) {
            // If more to display and options not activated activate it
            setShowMoreEvent((prevState) => ({
                ...prevState,
                isShowingMore: false,
                shouldShowMore: true
            }));
        }
    }, [socialMediaEvents, calendarDate, calendarData?.view, eventsPerDay, showMoreEvent]);

    return (
        <>
            <CalendarHeader
                ref={calendarRef}
                isGroup={isGroup}
                activeGroup={activeGroup}
                activeSocialProfile={activeSocialProfile}
                mediaLibraryGuidanceAllowed={
                    !isGroup &&
                    !isLoadingSchedulerPosts &&
                    (hasSchedulerPermission ||
                        (!hasSchedulerPermission &&
                            activeSocialProfile?.social_network_type === INSTAGRAM))
                }
            />

            <div className={classNames('flex flex-1')}>
                <div
                    className={classNames(
                        'fullcalendar-wrapper fullcalendar-wrapper-tree-structure relative flex-1'
                    )}>
                    {isLoadingSchedulerPosts || mediaLibraryState?.isMediaLibraryPostingLoading ? (
                        <div className="absolute z-[8] h-full w-full rounded-md bg-white/40 backdrop-blur-sm dark:bg-icoDarkMode-anthracite/80">
                            <Loader
                                className="absolute left-1/2 top-1/2 mr-3 h-8 w-8 text-icoBlue"
                                data-testid="calendar-view-loader"
                            />
                        </div>
                    ) : null}
                    {!calendarData?.isCalendarRemounting ? (
                        <FullCalendar
                            ref={calendarRef}
                            initialView={calendarData?.view}
                            height="100%"
                            slotDuration="01:00:00"
                            headerToolbar={null}
                            plugins={[interactionPlugin, timeGridPlugin, dayGridPlugin]}
                            now={dayjs().utc().tz(userTimezone).format()}
                            nowIndicatorContent={() => (
                                <span
                                    data-testid="now-indicator"
                                    className="block"
                                    ref={nowIndicatorRef}
                                />
                            )}
                            nowIndicatorDidMount={() => setNowMounted(true)}
                            titleFormat={{ day: 'numeric', month: 'short' }}
                            nowIndicator={true}
                            editable={true}
                            displayEventEnd={false}
                            forceEventDuration={true}
                            eventDurationEditable={false}
                            dayMaxEventRows={
                                !showMoreEvent?.state && calendarData?.view !== calendarViews?.month
                                    ? 2
                                    : null
                            }
                            moreLinkContent={({ text }) => {
                                return (
                                    <div className="w-full overflow-hidden text-ellipsis rounded-m bg-icoGray-100 px-2.5 py-2 text-xxs dark:bg-white/10 dark:text-white">{`${text}`}</div>
                                );
                            }}
                            moreLinkClick={() => {
                                setShowMoreEvent({
                                    state: true,
                                    shouldShowMore: true,
                                    isShowingMore: true
                                });
                                return 'showMore';
                            }}
                            eventStartEditable={true}
                            windowResize={({ view }) => {
                                if (
                                    view.type !== calendarViews.day &&
                                    window.matchMedia('(max-width: 1023px)').matches
                                ) {
                                    const calendarApi = calendarRef.current.getApi();
                                    calendarApi.changeView(calendarViews.day);

                                    dispatchCalendar({
                                        type: UPDATE_CALENDAR_VIEW,
                                        payload: calendarViews.day
                                    });
                                }
                            }}
                            locale={user?.settings?.platform_language ?? 'en'}
                            locales={frLocale}
                            firstDay={dayOfWeek[user?.settings?.first_day_week ?? 'monday']}
                            timeZone={userTimezone}
                            droppable={true}
                            slotLabelFormat={({ date }) => {
                                const hourDate = date.hour + ':' + date.minute;
                                const formattedTime = dayjs(`01/01/2021 ${hourDate}`).format(
                                    user?.settings?.time_format === '12' ? 'h:mm a' : 'H:mm'
                                );
                                return formattedTime;
                            }}
                            allDayContent={() => (
                                <UtcDisplay
                                    timezone={userTimezone}
                                    showMore={showMoreEvent}
                                    setShowMore={setShowMoreEvent}
                                />
                            )}
                            dayHeaderContent={({ date, isPast, isToday, view }) => (
                                <DayHeader
                                    date={date}
                                    isPast={isPast}
                                    isToday={isToday}
                                    viewType={view.type}
                                    locale={user?.settings?.platform_language ?? 'en'}
                                />
                            )}
                            slotLabelContent={({ text }) => <TimeLabel time={text} />}
                            datesSet={({ view }) => {
                                dispatchCalendar({
                                    type: UPDATE_CALENDAR_TITLE_DATE,
                                    payload: view.title
                                });

                                // Set start/end date to launch the request to get posts
                                const start = dayjs(view.currentStart).utc().format('YYYY-MM-DD');
                                const end = dayjs(view.currentEnd)
                                    .utc()
                                    .subtract('1', 'days')
                                    .format('YYYY-MM-DD');
                                setCalendarDate({ start, end });
                            }}
                            dateClick={({ dateStr, view, dayEl }) => {
                                if (
                                    view.type === calendarViews.month &&
                                    !dayEl.classList.contains('fc-day-other')
                                ) {
                                    const calendarApi = calendarRef.current.getApi();
                                    calendarApi.changeView(calendarViews.day, dateStr);
                                    // Update view select
                                    dispatchCalendar({
                                        type: UPDATE_CALENDAR_VIEW,
                                        payload: calendarViews.day
                                    });
                                }
                            }}
                            dayCellContent={(args) => {
                                const { view, dayNumberText, isToday, date } = args;
                                /*
                                 * View MONTH
                                 */
                                if (view.type === calendarViews.month) {
                                    return (
                                        <CalendarCellMonth
                                            dayNumberText={dayNumberText}
                                            isToday={isToday}
                                        />
                                    );
                                }

                                /*
                                 * Views WEEK & DAY
                                 */
                                return (
                                    <WeekDayCells
                                        activeSocialProfile={activeSocialProfile}
                                        activeGroup={activeGroup}
                                        isGroup={isGroup}
                                        onClick={(hour) => {
                                            if (activeSocialProfile || isGroup) {
                                                const slotDate = dayjs(date)
                                                    .utc()
                                                    .format('YYYY-MM-DD');
                                                const hourFormatted = hour < 10 ? `0${hour}` : hour;
                                                const utcFormat = dayjs
                                                    .tz(
                                                        `${slotDate} ${hourFormatted}:00`,
                                                        userTimezone
                                                    )
                                                    .format('Z');

                                                const dateFormatted = dayjs(
                                                    `${slotDate}T${hourFormatted}:00:00${utcFormat}`
                                                )
                                                    .utc()
                                                    .format();

                                                setParamsPostFunnel({
                                                    type: creation_type,
                                                    scheduledAt: dateFormatted,
                                                    socialProfiles: profiles
                                                });

                                                togglePostFunnel();
                                            }
                                        }}
                                        bestTimeToPostData={bestTimeToPostData}
                                        followersOnlineData={followersOnlineData}
                                        timeSlotsData={timeSlotsFormatted}
                                        {...args}
                                    />
                                );
                            }}
                            eventContent={({ event, view }) => {
                                if (event?.source?.internalEventSource?.publicId === 'SMC') {
                                    return (
                                        <SocialMediaEvent
                                            userDateFormatSettings={user?.settings.date_format}
                                            event={{
                                                user_id: event._def.extendedProps?.user_id,
                                                id: event._def.publicId,
                                                title: event._def.title,
                                                hashtags: event._def.extendedProps?.hashtags ?? [],
                                                start_date: event._def.extendedProps.startDate,
                                                end_date: event._def.extendedProps.endDate
                                            }}
                                        />
                                    );
                                }

                                if (event?.source?.internalEventSource?.publicId === 'POST') {
                                    const { startRef } = event._def.extendedProps;
                                    const idPost = event._def.publicId;
                                    const key =
                                        view.type === calendarViews.month ? 'days' : 'hours';
                                    const datePost =
                                        view.type === calendarViews.month
                                            ? startRef.split(' ')[0]
                                            : startRef;
                                    const stackedPosts =
                                        schedulerStackedPosts?.[key]?.[datePost] ?? [];
                                    const nbStackedPosts = stackedPosts?.length ?? 0;

                                    if (nbStackedPosts > 1) {
                                        const stackedPostsIds = stackedPosts.map(({ id }) =>
                                            parseInt(id)
                                        );
                                        const postIndex = stackedPostsIds.indexOf(parseInt(idPost));

                                        if (postIndex === nbStackedPosts - 1) {
                                            return (
                                                <PostEventStacked
                                                    post={{
                                                        ...event._def.extendedProps,
                                                        id: idPost
                                                    }}
                                                    network={
                                                        activatedSocialProfiles?.find(
                                                            (sp) =>
                                                                sp.id ===
                                                                event._def.extendedProps
                                                                    ?.social_profile_id
                                                        )?.social_network_type
                                                    }
                                                    timeFormat={user?.settings?.time_format}
                                                    timezone={userTimezone}
                                                    stackedPosts={stackedPosts}
                                                    settings={({ dataPost, disablePopover }) => {
                                                        return (
                                                            <PostEventSettingsPopover
                                                                post={dataPost}
                                                                onPreview={(post) => {
                                                                    setParamsPostPreview({
                                                                        postId: post?.id,
                                                                        socialProfileId:
                                                                            post?.social_profile_id
                                                                    });
                                                                    togglePostPreview();
                                                                }}
                                                                userTimezone={userTimezone}
                                                                onClickEditPost={(post) => {
                                                                    setParamsPostFunnel({
                                                                        type: 'edit',
                                                                        socialProfileId:
                                                                            post.social_profile_id,
                                                                        postId: post.id
                                                                    });

                                                                    togglePostFunnel();
                                                                }}
                                                                onClickReSchedulePost={(post) => {
                                                                    setParamsPostFunnel({
                                                                        type: 'duplicate',
                                                                        socialProfileId:
                                                                            post?.social_profile_id,
                                                                        postId: post.id
                                                                    });

                                                                    togglePostFunnel();
                                                                }}
                                                                mutateSchedulerPosts={
                                                                    mutateSchedulerPosts
                                                                }
                                                                disablePopover={disablePopover}
                                                            />
                                                        );
                                                    }}
                                                    clickAction={{
                                                        isAvailable: true,
                                                        edit: (post) => {
                                                            setParamsPostFunnel({
                                                                type: 'edit',
                                                                postId: post.id,
                                                                socialProfileId:
                                                                    post.social_profile_id
                                                            });

                                                            togglePostFunnel();
                                                        },
                                                        preview: (post) => {
                                                            setParamsPostPreview({
                                                                postId: post?.id,
                                                                socialProfileId:
                                                                    post?.social_profile_id
                                                            });
                                                            togglePostPreview();
                                                        }
                                                    }}
                                                    isGroup={isGroup}
                                                    spGroups={spGroups}
                                                />
                                            );
                                        }

                                        return;
                                    } else {
                                        const dataPost = {
                                            ...event._def.extendedProps,
                                            id: idPost
                                        };
                                        return (
                                            <PostEvent
                                                post={dataPost}
                                                timeFormat={user?.settings?.time_format}
                                                isReadOnly={dataPost?.read_only ?? false}
                                                timezone={userTimezone}
                                                settings={
                                                    <PostEventSettingsPopover
                                                        post={dataPost}
                                                        onPreview={(post) => {
                                                            setParamsPostPreview({
                                                                postId: post?.id,
                                                                socialProfileId:
                                                                    post?.social_profile_id
                                                            });
                                                            togglePostPreview();
                                                        }}
                                                        userTimezone={userTimezone}
                                                        onClickEditPost={(post) => {
                                                            setParamsPostFunnel({
                                                                type: 'edit',
                                                                postId: post.id,
                                                                socialProfileId:
                                                                    post.social_profile_id
                                                            });

                                                            togglePostFunnel();
                                                        }}
                                                        onClickReSchedulePost={(post) => {
                                                            setParamsPostFunnel({
                                                                type: 'duplicate',
                                                                postId: post.id,
                                                                socialProfileId:
                                                                    post?.social_profile_id
                                                            });

                                                            togglePostFunnel();
                                                        }}
                                                        mutateSchedulerPosts={mutateSchedulerPosts}
                                                    />
                                                }
                                                clickAction={{
                                                    isAvailable: true,
                                                    edit: (post) => {
                                                        setParamsPostFunnel({
                                                            type: 'edit',
                                                            postId: post.id,
                                                            socialProfileId: post.social_profile_id
                                                        });

                                                        togglePostFunnel();
                                                    },
                                                    preview: (post) => {
                                                        setParamsPostPreview({
                                                            postId: post?.id,
                                                            socialProfileId: post?.social_profile_id
                                                        });
                                                        togglePostPreview();
                                                    }
                                                }}
                                                isGroup={isGroup}
                                            />
                                        );
                                    }
                                }

                                if (event?._def?.publicId !== '') {
                                    const dataPost = {
                                        ...event._def.extendedProps,
                                        id: event._def.publicId
                                    };

                                    const utc = dayjs(event?._instance?.range?.start)
                                        .utc()
                                        .tz(userTimezone)
                                        .format('Z');
                                    const formatStart = dayjs(event?._instance?.range?.start)
                                        .utc()
                                        .format('YYYY-MM-DDTHH:mm:ss');
                                    const dateSlot = dayjs(formatStart + utc).utc();
                                    const isFuture = dateSlot.isSameOrAfter(
                                        dayjs(
                                            dayjs().add(5, 'minute').format('YYYY-MM-DD HH:00:00')
                                        ).utc()
                                    );

                                    if (isFuture) {
                                        return (
                                            <PostEvent
                                                isPlaceholder={mediaLibraryState.isActive}
                                                post={dataPost}
                                                timeFormat={user?.settings?.time_format}
                                                timezone={userTimezone}
                                                isGroup={isGroup}
                                            />
                                        );
                                    } else {
                                        return null;
                                    }
                                }

                                if (event?._def?.publicId === '') {
                                    return (
                                        <div className="flex h-12 w-full bg-white p-0.5">
                                            <div className="relative flex w-full items-center justify-center overflow-hidden rounded-m bg-icoGray-400 dark:bg-icoDarkMode-goudron/40">
                                                <Icon icon="plus" className="text-sm text-white" />
                                            </div>
                                        </div>
                                    );
                                }

                                return null;
                            }}
                            eventDidMount={({ event, view }) => {
                                if (event?.source?.internalEventSource?.publicId === 'POST') {
                                    const { startRef } = event._def.extendedProps;
                                    const idPost = event._def.publicId;
                                    const key =
                                        view.type === calendarViews.month ? 'days' : 'hours';
                                    const datePost =
                                        view.type === calendarViews.month
                                            ? startRef.split(' ')[0]
                                            : startRef;
                                    const stackedPosts = schedulerStackedPosts[key][datePost] ?? [];
                                    const nbStackedPosts = stackedPosts?.length ?? 0;
                                    if (nbStackedPosts > 1) {
                                        const stackedPostsIds = stackedPosts.map(({ id }) =>
                                            parseInt(id)
                                        );
                                        const postIndex = stackedPostsIds.indexOf(parseInt(idPost));
                                        if (postIndex !== nbStackedPosts - 1) {
                                            event.setProp('display', 'none');
                                        } else {
                                            event.setProp('startEditable', false);
                                        }
                                    }
                                }
                            }}
                            eventAllow={({ startStr }, draggedEvent) => {
                                if (draggedEvent.source === null) {
                                    document
                                        .querySelectorAll('div[data-tippy-root]')
                                        .forEach((el) => {
                                            el._tippy.hide();
                                        });
                                }

                                if (
                                    draggedEvent.source?.id === 'POST' ||
                                    draggedEvent.source === null // Stacked events source = null
                                ) {
                                    const isReadOnly = draggedEvent?._def?.extendedProps?.read_only;
                                    if (isReadOnly) return false;

                                    const utc = dayjs(startStr).utc().tz(userTimezone).format('Z');

                                    // Deactivate drop in a slot if it's in the past
                                    if (
                                        draggedEvent._context.viewSpec.type === calendarViews.month
                                    ) {
                                        const currentHour = dayjs()
                                            .utc()
                                            .tz(userTimezone)
                                            .format('HH:mm:ss');
                                        const dateSlot = dayjs(
                                            dayjs(startStr).format('YYYY-MM-DD') + 'T' + currentHour
                                        );

                                        const dateSlotYear = dateSlot.format('YYYY');
                                        const dateSlotMonth = dateSlot.format('MM');
                                        const dateSlotDay = dateSlot.format('DD');

                                        const currentDate = dayjs().utc().tz(userTimezone);
                                        const currentDateYear = currentDate.format('YYYY');
                                        const currentDateMonth = currentDate.format('MM');
                                        const currentDateDay = currentDate.format('DD');

                                        if (dateSlotYear > currentDateYear) return true;

                                        if (
                                            currentDateYear === dateSlotYear &&
                                            (currentDateMonth === dateSlotMonth ||
                                                parseInt(currentDateMonth) <
                                                    parseInt(dateSlotMonth))
                                        ) {
                                            if (
                                                parseInt(dateSlotDay) === parseInt(currentDateDay)
                                            ) {
                                                const postScheduledAtTz = dayjs(
                                                    draggedEvent?._def?.extendedProps?.scheduled_at
                                                ).utc();

                                                const datePostOnSlot = dayjs(
                                                    `${dateSlot.format(
                                                        'YYYY-MM-DD'
                                                    )}T${postScheduledAtTz.format('HH:mm:ss')}`
                                                );

                                                return datePostOnSlot.isSameOrAfter(
                                                    dayjs(
                                                        dayjs()
                                                            .add(30, 'minute')
                                                            .tz(userTimezone)
                                                            .format('YYYY-MM-DD 00:00:00')
                                                    ).utc()
                                                );
                                            }

                                            // If next months selected
                                            if (
                                                parseInt(currentDateMonth) < parseInt(dateSlotMonth)
                                            ) {
                                                return true;
                                            }

                                            return parseInt(dateSlotDay) > parseInt(currentDateDay);
                                        }
                                    } else {
                                        const dateSlot = dayjs(startStr + utc).utc();
                                        return dateSlot.isSameOrAfter(
                                            dayjs(
                                                dayjs()
                                                    .add(5, 'minute')
                                                    .format('YYYY-MM-DD HH:00:00')
                                            ).utc()
                                        );
                                    }
                                }

                                return false;
                            }}
                            eventReceive={({ event }) => {
                                const idPost = event?._def?.publicId;

                                if (idPost) {
                                    const oldDataPost = event?._def?.extendedProps;

                                    if (oldDataPost?.cross_post_id || oldDataPost?.master_id) {
                                        setCrossPostDragAndDropModalData((prevState) => ({
                                            ...prevState,
                                            event: event,
                                            oldStart: event?.start,
                                            oldEnd: event?.end
                                        }));

                                        toggleCrosspostDragAndDropModal();
                                    } else {
                                        eventsHandling({
                                            event,
                                            schedulerPosts,
                                            mutateSchedulerPosts,
                                            timezone: userTimezone
                                        });
                                    }
                                } else {
                                    eventsHandling({
                                        event,
                                        schedulerPosts,
                                        mutateSchedulerPosts,
                                        timezone: userTimezone
                                    });
                                }
                                event.remove();
                            }}
                            eventDrop={({ event, oldEvent }) => {
                                const idPost = event?._def?.publicId;

                                if (idPost) {
                                    const oldDataPost = event?._def?.extendedProps;

                                    if (oldDataPost?.cross_post_id || oldDataPost?.master_id) {
                                        setCrossPostDragAndDropModalData((prevState) => ({
                                            ...prevState,
                                            event: event,
                                            oldStart: oldEvent?.start,
                                            oldEnd: oldEvent?.end
                                        }));

                                        toggleCrosspostDragAndDropModal();
                                    } else {
                                        eventsHandling({
                                            event,
                                            schedulerPosts,
                                            mutateSchedulerPosts,
                                            timezone: userTimezone
                                        });
                                    }
                                }
                            }}
                            drop={onDropMedia}
                        />
                    ) : null}
                </div>
                {!isGroup ? (
                    <AnimatePresence initial={!calendarData.openMediaLibrary.active}>
                        {calendarData.openMediaLibrary.active ? (
                            <DndContext
                                onDragStart={(event) => {
                                    setMediaLibraryState((prevState) => ({
                                        ...prevState,
                                        activeMedia: event.active?.data?.current,
                                        isActive: true
                                    }));
                                }}
                                onDragEnd={() =>
                                    setMediaLibraryState((prevState) => ({
                                        ...prevState,
                                        isActive: false
                                    }))
                                }>
                                <div className="relative h-full w-full max-w-[192px] xl:max-w-[290px]">
                                    <motion.div
                                        className={classNames(
                                            'absolute left-0 top-0 z-2 h-full w-full origin-top-right'
                                        )}
                                        initial={{ opacity: 0, scale: 0.85 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.85 }}
                                        transition={{
                                            duration: 0.25
                                        }}
                                        onAnimationStart={() =>
                                            setMediaLibraryState((prevState) => ({
                                                ...prevState,
                                                transitionInProgress: true
                                            }))
                                        }
                                        onAnimationComplete={() =>
                                            setMediaLibraryState((prevState) => ({
                                                ...prevState,
                                                transitionInProgress: false
                                            }))
                                        }>
                                        <MediaLibraryWrapper
                                            mediaLibraryState={mediaLibraryState}
                                            onDropMedia={onDropMedia}
                                            mediaLibraryError={paramsMediaLibraryError}
                                            toggleMediaLibraryError={toggleMediaLibraryError}
                                            isOpenMediaLibraryError={isOpenMediaLibraryError}
                                            size="small"
                                            onDestroy={() => {
                                                if (calendarRef?.current) {
                                                    const calendarApi =
                                                        calendarRef.current.getApi();
                                                    calendarApi.updateSize();
                                                }
                                            }}
                                            onClose={() =>
                                                dispatchCalendar({
                                                    type: UPDATE_CALENDAR_MEDIA_LIBRARY,
                                                    payload: false
                                                })
                                            }
                                        />
                                    </motion.div>
                                </div>
                                {createPortal(
                                    <DragOverlay>
                                        {mediaLibraryState.isActive ? (
                                            <div className="square draggable-overlay">
                                                <ImageMedia media={mediaLibraryState.activeMedia} />
                                            </div>
                                        ) : null}
                                    </DragOverlay>,
                                    document.body
                                )}
                            </DndContext>
                        ) : null}
                    </AnimatePresence>
                ) : null}
            </div>

            <SchedulerFunnel
                isOpen={isOpenPostFunnel}
                toggle={togglePostFunnel}
                settings={paramsPostFunnel}
                callbackSuccess={() => mutateSchedulerPosts(null)}
            />

            <ModalPostPreview
                isOpen={isOpenPostPreview}
                toggle={togglePostPreview}
                postId={paramsPostPreview?.postId ?? null}
                spId={paramsPostPreview?.socialProfileId ?? null}
                onClickReSchedulePost={() => {
                    setParamsPostFunnel({
                        type: 'duplicate',
                        socialProfileId: paramsPostPreview?.socialProfileId,
                        postId: paramsPostPreview?.postId
                    });
                    togglePostFunnel();
                    togglePostPreview();
                }}
            />
            <ModalDragAndDropPostCrosspost
                isOpen={isOpenCrosspostDragAndDropModal}
                posts={[crossPostDragAndDropModalData?.event?._def?.extendedProps]}
                onClickThisProfile={() => {
                    eventsHandling({
                        event: crossPostDragAndDropModalData?.event,
                        schedulerPosts,
                        mutateSchedulerPosts,
                        timezone: userTimezone,
                        updateCrossPosts: false
                    });
                    toggleCrosspostDragAndDropModal();
                }}
                onClickAllProfiles={() => {
                    eventsHandling({
                        event: crossPostDragAndDropModalData?.event,
                        schedulerPosts,
                        mutateSchedulerPosts,
                        timezone: userTimezone,
                        updateCrossPosts: true
                    });
                    toggleCrosspostDragAndDropModal();
                }}
                cancel={() => {
                    // Cancel drop
                    crossPostDragAndDropModalData?.event.setDates(
                        crossPostDragAndDropModalData?.oldStart,
                        crossPostDragAndDropModalData?.oldEnd
                    );
                    toggleCrosspostDragAndDropModal();
                }}
            />
        </>
    );
};

CalendarView.propTypes = {
    needReloadScheduledPosts: PropTypes.bool,
    setNeedReloadScheduledPosts: PropTypes.func,
    blockView: PropTypes.bool,
    hasSchedulerPermission: PropTypes.bool
};
export default CalendarView;
